import { Component } from '@theme/component';
import ageVerificationService from '@theme/age-verification-service';


/**
 * Age Verification Modal Component
 * 
 * Handles age verification modal display and verification events
 * 
 * @typedef {object} AgeVerificationModalRefs
 * @property {HTMLElement} dialogComponent - The dialog component
 * @extends Component<AgeVerificationModalRefs>
 */
class AgeVerificationModal extends Component {
  requiredRefs = ['dialogComponent'];

  /** @type {((event: Event) => void) | null} */
  #showHandler = null;

  /** @type {boolean} */
  #showingWarningState = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupEventListeners();
  }

  #setupEventListeners() {
    this.#showHandler = this.#onShowModal.bind(this);

    if (this.#showHandler) {
      document.addEventListener('age-verification:show', this.#showHandler);
    }
  }

  #cleanupEventListeners() {
    if (this.#showHandler) {
      document.removeEventListener('age-verification:show', this.#showHandler);
      this.#showHandler = null;
    }
  }

  /**
   * Handles confirm button click
   * @param {Event} event
   */
  onConfirmClick(event) {
    event.preventDefault();
    event.stopPropagation();

    ageVerificationService.onVerified();

    const { dialogComponent } = this.refs;
    /** @type {HTMLElement & { closeDialog?: () => void } | undefined} */
    const dialog = dialogComponent;
    if (dialog && typeof dialog.closeDialog === 'function') {
      dialog.closeDialog();
    }
    
    this.#showingWarningState = false;
  }

  #onShowModal() {
    this.#showingWarningState = false;
    this.#updateModalContent();
    this.#updateDialogCloseSettings();

    const { dialogComponent } = this.refs;
    /** @type {HTMLElement & { showDialog?: () => void } | undefined} */
    const dialog = dialogComponent;
    if (dialog && typeof dialog.showDialog === 'function') {
      dialog.showDialog();
    }
  }

  #updateDialogCloseSettings() {
    const { dialogComponent } = this.refs;
    if (!(dialogComponent instanceof HTMLElement)) return;

    if (!this.#showingWarningState) {
      dialogComponent.setAttribute('dialog-close-on-escape-press', 'false');
      dialogComponent.setAttribute('dialog-close-on-outside-click', 'false');
    } else {
      dialogComponent.setAttribute('dialog-close-on-escape-press', 'true');
      dialogComponent.setAttribute('dialog-close-on-outside-click', 'true');
    }
  }

  /**
   * Handles close button click
   * @param {Event} event
   */
  onCloseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.#showingWarningState) {
      this.#showingWarningState = true;
      this.#updateModalContent();
      this.#updateDialogCloseSettings();
      return;
    }
    
    const { dialogComponent } = this.refs;
    /** @type {HTMLElement & { closeDialog?: () => void } | undefined} */
    const dialog = dialogComponent;
    if (dialog && typeof dialog.closeDialog === 'function') {
      dialog.closeDialog();
    }
    
    this.#showingWarningState = false;
  }

  #updateModalContent() {
    const initialContent = this.querySelector('[ref="initialContent"]');
    const warningContent = this.querySelector('[ref="warningContent"]');
    
    if (!(initialContent instanceof HTMLElement) || !(warningContent instanceof HTMLElement)) return;
    
    if (this.#showingWarningState) {
      initialContent.classList.add('hidden');
      initialContent.classList.remove('flex');
      warningContent.classList.remove('hidden');
      warningContent.classList.add('flex');
    } else {
      initialContent.classList.remove('hidden');
      initialContent.classList.add('flex');
      warningContent.classList.add('hidden');
      warningContent.classList.remove('flex');
    }
  }

}

if (!customElements.get('age-verification-modal')) {
  customElements.define('age-verification-modal', AgeVerificationModal);
}
